import type { IUser, UserExtension } from "stores/auth"
import type { ModuleFlag, Shell } from "./types"

/** The store types its user this way — see AuthState in stores/auth.ts. */
export type SessionUser = (IUser & Partial<UserExtension>) | null | undefined

/**
 * The single access rule for global search.
 *
 * `can` exists to be behaviour-identical to hooks/usePermissions.tsx. That hook
 * is the app's real rule; this is a pure restatement of it so the registry can
 * be filtered without rendering a hook per entry. If the hook changes, change
 * this and its tests together.
 */
export interface AccessContext {
  /** True when the user holds this permission alias, or has the admin bypass. */
  can: (permission: string) => boolean
  /**
   * ANY-of, not ALL. Mirrors RequireRoutePermit's `permission?.some(Boolean)`.
   * Both directions of error are costly: ALL silently hides legitimate
   * destinations, anything looser leaks them.
   */
  canAny: (permissions: string[]) => boolean
  /** True only when the org-level switch is explicitly on. */
  moduleOn: (flag: ModuleFlag) => boolean
  /** Whether the user bypasses permission checks entirely. */
  hasAdminBypass: boolean
  /** The shell this context was built for. */
  shell: Shell
}

export function buildAccessContext(
  user: SessionUser,
  shell: Shell
): AccessContext {
  // Mirrors usePermissions line for line so the two can be diffed by eye.
  //
  // Deliberately NOT consulted: IUser also carries `isAdmin` and
  // `isConsultant`. usePermissions ignores both, so folding them in here would
  // widen the bypass beyond the app's current behaviour — which is a leak, and
  // breaks the "existing admin bypass behavior" acceptance criterion. The names
  // are tempting; leave them alone.
  const isPrimaryAdmin =
    user?.isPrimaryAdmin === true ||
    user?.userRoles?.includes("Primary Admin") === true
  const isSecondaryAdmin =
    user?.isSecondaryAdmin === true ||
    user?.userRoles?.includes("Secondary Admin") === true

  const hasAdminBypass = isPrimaryAdmin || isSecondaryAdmin

  // Exact alias comparison, as in usePermissions. No trimming or lower-casing:
  // normalising here would match aliases the app itself would reject, which
  // widens access rather than fixing data.
  const can = (permission: string): boolean =>
    hasAdminBypass ||
    user?.userPermissions?.some((held) => held.alias === permission) === true

  // An empty list is false, matching RequireRoutePermit: usePermissions maps
  // [] to [], and [].some(Boolean) is false. Deliberately open destinations are
  // handled by the registry's `ungated` field, not by relaxing this.
  const canAny = (permissions: string[]): boolean =>
    Array.isArray(permissions) && permissions.some(can)

  // Strict `=== true`, so anything absent or non-boolean reads as off.
  //
  // The flags are typed as required on IUser, but the value here comes from a
  // persisted, rehydrated blob and from API payloads that predate some of these
  // fields, so undefined does occur in practice — and
  // `isNewPerformanceEnabled` is genuinely optional on UserExtension.
  //
  // Failing closed is the cheaper error in both directions: a false negative
  // hides a destination the org owns and the user navigates by hand, while a
  // false positive renders a label for a module the org does not have, which
  // turns search into an org-structure oracle. The server middleware remains
  // the real boundary either way.
  const moduleOn = (flag: ModuleFlag): boolean => user?.[flag] === true

  return { can, canAny, moduleOn, hasAdminBypass, shell }
}
